import React, { useEffect, useRef, useState } from 'react';
import Grid from '@mui/material/GridLegacy';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Container,
  TextField,
  Button,
  Alert,
  Typography,
  Chip,
  Divider,
  LinearProgress,
} from '@mui/material';
import {
  Search as SearchIcon,
  CameraAlt as CameraIcon,
  Stop as StopIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Info as InfoIcon,
  Sensors as ScanGunIcon,
  DirectionsCar as VehicleIcon,
  Category as CategoryIcon,
  Factory as MakeIcon,
  CalendarToday as CalendarIcon,
  Person as PersonIcon,
  Badge as CnicIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  EventAvailable as EventAvailableIcon,
  EventBusy as EventBusyIcon,
  Description as DescriptionIcon,
  AltRoute as RouteIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import apiClient from '../services/apiClient';

const PermitSearch = () => {
  const { t } = useTranslation();
  const [searchType, setSearchType] = useState('vehicle');
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [scanMessage, setScanMessage] = useState('');
  const [rfidWarning, setRfidWarning] = useState('');
  const [rfidGunListening, setRfidGunListening] = useState(false);
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [cameraLoading, setCameraLoading] = useState(false);
  const [cameraSupported, setCameraSupported] = useState(true);
  const [cameraInfo, setCameraInfo] = useState('');
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const scanIntervalRef = useRef(null);
  const queryInputRef = useRef(null);

  useEffect(() => {
    if (!navigator?.mediaDevices?.getUserMedia || typeof window?.BarcodeDetector === 'undefined') {
      setCameraSupported(false);
    }

    return () => {
      stopScanner();
    };
  }, []);

  const buildSearchParams = (type, query) => {
    switch (type) {
      case 'vehicle':
        return { vehicle_number: query };
      case 'cnic':
        return { cnic: query };
      case 'permit_number':
        return { permit_number: query };
      case 'permit_id':
        return { permit_id: query };
      case 'rfid':
        return { rfid_tag: query };
      default:
        return { vehicle_number: query };
    }
  };

  const executeSearch = async (type, query) => {
    if (!query.trim()) {
      setError(t('permitSearch.errorEnterQuery'));
      return;
    }

    setLoading(true);
    setError('');
    setRfidWarning('');
    setRfidGunListening(false);
    setResults([]);
    setSearched(true);

    try {
      const params = buildSearchParams(type, query.trim());
      const response = await apiClient.get('/permits/public_search/', { params });

      if (response.data.rfid_status === 'inactive') {
        // Inactive tag: always show the warning. Permit details only come
        // back from the API when the viewer is authorized to see them
        // (staff / rfid_read) - everyone else gets count: 0 with no results,
        // which is deliberate, not a "nothing found" case.
        setRfidWarning(response.data.rfid_warning || t('permitSearch.rfidInactiveWarning'));
        setResults(response.data.results || []);
      } else if (response.data.count === 0) {
        setError(response.data.message || t('permitSearch.errorNoPermitsFound'));
        setResults([]);
      } else {
        setResults(response.data.results);
      }
    } catch (err) {
      setError(
        err.response?.data?.error ||
        err.response?.data?.detail ||
        t('permitSearch.errorSearchFailed')
      );
      setResults([]);
      console.error('Search error:', err);
    } finally {
      setLoading(false);
    }
  };

  const parseScannedValue = (raw) => {
    const value = (raw || '').trim();
    if (!value) return null;

    if (value.startsWith('PERMIT|')) {
      const parts = value.split('|');
      return {
        type: 'vehicle',
        query: parts[2] || '',
        message: t('permitSearch.scanMsgQr'),
      };
    }

    if (/^\d+$/.test(value)) {
      return {
        type: 'permit_id',
        query: value,
        message: t('permitSearch.scanMsgNumeric'),
      };
    }

    return {
      type: 'permit_number',
      query: value,
      message: t('permitSearch.scanMsgCode'),
    };
  };

  const handleScanResult = async (rawValue) => {
    const parsed = parseScannedValue(rawValue);
    if (!parsed || !parsed.query) {
      setError(t('permitSearch.errorScanInvalid'));
      return;
    }

    stopScanner();
    setScanMessage(parsed.message);
    setSearchType(parsed.type);
    setSearchQuery(parsed.query);
    await executeSearch(parsed.type, parsed.query);
  };

  const startScanner = async () => {
    setError('');
    setScanMessage('');
    setCameraLoading(true);

    if (!cameraSupported) {
      setError(t('permitSearch.errorCameraUnsupported'));
      setCameraLoading(false);
      return;
    }

    try {
      const constraintCandidates = [
        {
          video: {
            facingMode: { exact: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        },
        {
          video: {
            facingMode: { ideal: 'environment' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        },
        {
          video: {
            facingMode: { ideal: 'user' },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        },
        { video: true, audio: false },
      ];

      let stream = null;
      let selectedConstraints = null;
      for (const constraints of constraintCandidates) {
        try {
          stream = await navigator.mediaDevices.getUserMedia(constraints);
          selectedConstraints = constraints;
          break;
        } catch (err) {
          // try next constraint profile
        }
      }

      if (!stream) {
        throw new Error('Unable to initialize camera stream');
      }

      streamRef.current = stream;
      const firstTrack = stream.getVideoTracks()[0];
      setCameraInfo(firstTrack?.label || t('permitSearch.cameraConnected'));
      if (videoRef.current) {
        videoRef.current.setAttribute('playsinline', 'true');
        videoRef.current.setAttribute('muted', 'true');
        videoRef.current.muted = true;
        videoRef.current.autoplay = true;
        videoRef.current.srcObject = stream;

        await new Promise((resolve, reject) => {
          if (!videoRef.current) {
            reject(new Error('Video element unavailable'));
            return;
          }

          const onLoaded = async () => {
            try {
              await videoRef.current.play();
              resolve();
            } catch (playErr) {
              reject(playErr);
            }
          };

          videoRef.current.onloadedmetadata = onLoaded;
          videoRef.current.onloadeddata = onLoaded;
          videoRef.current.oncanplay = onLoaded;

          if (videoRef.current.readyState >= 1) {
            onLoaded();
          }

          setTimeout(() => {
            if (!videoRef.current || (videoRef.current.videoWidth > 0 && videoRef.current.videoHeight > 0)) {
              return;
            }
            reject(new Error('Camera stream opened but no video frames received'));
          }, 2500);
        });

        if (videoRef.current.videoWidth === 0 || videoRef.current.videoHeight === 0) {
          throw new Error('Camera opened but preview frames are not available');
        }
      }

      const detector = new window.BarcodeDetector({
        formats: ['qr_code', 'code_128', 'code_39', 'ean_13', 'upc_a'],
      });
      scanIntervalRef.current = setInterval(async () => {
        try {
          if (!videoRef.current || videoRef.current.readyState < 2) return;
          const barcodes = await detector.detect(videoRef.current);
          if (barcodes && barcodes.length > 0 && barcodes[0].rawValue) {
            await handleScanResult(barcodes[0].rawValue);
          }
        } catch (scanError) {
          console.error('Scan detection error:', scanError);
        }
      }, 700);

      setScannerActive(true);
      setCameraLoading(false);
    } catch (cameraError) {
      console.error('Camera access error:', cameraError);
      setError(t('permitSearch.errorCameraPreview'));
      stopScanner();
      setCameraLoading(false);
    }
  };

  const stopScanner = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }

    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }

    setScannerActive(false);
    setCameraLoading(false);
    setCameraInfo('');
  };

  // Calculate days until expiry
  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  // Get status color and message.
  // The permit's stored `status` is authoritative — e.g. a 'pending' permit
  // isn't "expired" just because its requested valid_to date has already
  // passed while awaiting approval. The valid_to-based Expired/Expiring Soon
  // downgrade only makes sense for permits that are actually 'active'.
  const getStatusInfo = (permit) => {
    if (permit.status === 'expired') {
      return { label: t('common.expired'), color: 'error', icon: <CancelIcon /> };
    }
    if (permit.status === 'cancelled') {
      return { label: t('common.cancelled'), color: 'error', icon: <CancelIcon /> };
    }
    if (permit.status === 'active') {
      const daysLeft = getDaysUntilExpiry(permit.valid_to);
      if (daysLeft < 0) {
        return { label: t('common.expired'), color: 'error', icon: <CancelIcon /> };
      }
      if (daysLeft < 30) {
        return { label: t('permitSearch.statusExpiringSoon'), color: 'warning', icon: <ScheduleIcon /> };
      }
      return { label: t('common.active'), color: 'success', icon: <CheckCircleIcon /> };
    }
    return { label: t(`common.${permit.status}`, { defaultValue: permit.status }), color: 'default', icon: <InfoIcon /> };
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    await executeSearch(searchType, searchQuery);
  };

  const handleClear = () => {
    setSearchQuery('');
    setResults([]);
    setError('');
    setScanMessage('');
    setRfidWarning('');
    setRfidGunListening(false);
    setSearched(false);
    stopScanner();
  };

  const handleStartGunScan = () => {
    setError('');
    setRfidWarning('');
    setSearchQuery('');
    setRfidGunListening(true);
    setTimeout(() => queryInputRef.current?.focus(), 0);
  };

  const statusInfo = results.length > 0 ? getStatusInfo(results[0]) : null;

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 1, color: '#1976d2' }}>
          {t('permitSearch.title')}
        </Typography>
        <Typography variant="body1" sx={{ color: 'text.secondary' }}>
          {t('permitSearch.subtitle')}
        </Typography>
      </Box>

      {/* Search Card */}
      <Card sx={{ mb: 4 }}>
        <CardHeader
          title={t('permitSearch.cardTitle')}
          subheader={t('permitSearch.cardSubheader')}
          sx={{ bgcolor: '#f5f5f5' }}
        />
        <CardContent>
          <form onSubmit={handleSearch}>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label={t('permitSearch.searchBy')}
                  value={searchType}
                  onChange={(e) => { setSearchType(e.target.value); setRfidGunListening(false); }}
                  SelectProps={{ native: true }}
                >
                  <option value="vehicle">{t('permitSearch.vehicleOption')}</option>
                  <option value="cnic">{t('permitSearch.cnicOption')}</option>
                  <option value="permit_number">{t('permitSearch.permitNumberOption')}</option>
                  <option value="permit_id">{t('permitSearch.permitIdOption')}</option>
                  <option value="rfid">{t('permitSearch.rfidOption')}</option>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    inputRef={queryInputRef}
                    label={
                      searchType === 'vehicle'
                        ? t('permitSearch.vehicleNumberLabel')
                        : searchType === 'cnic'
                          ? t('permitSearch.cnicLabel')
                          : searchType === 'permit_number'
                            ? t('permitSearch.permitNumberLabel')
                            : searchType === 'permit_id'
                              ? t('permitSearch.permitIdLabel')
                              : t('permitSearch.rfidLabel')
                    }
                    placeholder={
                      searchType === 'vehicle'
                        ? t('permitSearch.vehicleNumberPlaceholder')
                        : searchType === 'cnic'
                          ? t('permitSearch.cnicPlaceholder')
                          : searchType === 'permit_number'
                            ? t('permitSearch.permitNumberPlaceholder')
                            : searchType === 'permit_id'
                              ? t('permitSearch.permitIdPlaceholder')
                              : t('permitSearch.rfidPlaceholder')
                    }
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    disabled={loading}
                    sx={searchType === 'rfid' && rfidGunListening ? {
                      '& .MuiOutlinedInput-root fieldset': { borderColor: '#00695c', borderWidth: 2 },
                    } : undefined}
                  />
                  {searchType === 'rfid' && (
                    <Button
                      variant={rfidGunListening ? 'contained' : 'outlined'}
                      startIcon={<ScanGunIcon />}
                      onClick={handleStartGunScan}
                      disabled={loading}
                      sx={rfidGunListening ? {
                        whiteSpace: 'nowrap',
                        bgcolor: '#00695c',
                        '&:hover': { bgcolor: '#004d40' },
                        '& .MuiButton-startIcon svg': { animation: 'rfid-pulse 1.4s ease-in-out infinite' },
                        '@keyframes rfid-pulse': {
                          '0%, 100%': { opacity: 1, transform: 'scale(1)' },
                          '50%': { opacity: 0.6, transform: 'scale(1.15)' },
                        },
                      } : {
                        whiteSpace: 'nowrap',
                        borderColor: '#00695c',
                        color: '#00695c',
                        '&:hover': { bgcolor: '#e0f2f1', borderColor: '#004d40' },
                        '& .MuiButton-startIcon': { transition: 'transform 0.2s ease-out' },
                        '&:hover .MuiButton-startIcon': { transform: 'scale(1.15)' },
                      }}
                    >
                      {rfidGunListening ? t('permitSearch.scanWithGunListeningBtn') : t('permitSearch.scanWithGunBtn')}
                    </Button>
                  )}
                </Box>
                {searchType === 'rfid' && rfidGunListening && (
                  <Typography variant="caption" sx={{ display: 'block', color: '#00695c', mt: 0.5 }}>
                    {t('permitSearch.scanWithGunListening')}
                  </Typography>
                )}
              </Grid>
            </Grid>

            <Box sx={{ mb: 3 }}>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                <Button
                  variant="outlined"
                  startIcon={<CameraIcon />}
                  onClick={startScanner}
                  disabled={loading || scannerActive || !cameraSupported}
                >
                  {t('permitSearch.startCameraScan')}
                </Button>
                <Button
                  variant="outlined"
                  color="warning"
                  startIcon={<StopIcon />}
                  onClick={stopScanner}
                  disabled={!scannerActive}
                >
                  {t('permitSearch.stopCamera')}
                </Button>
              </Box>

              {scannerActive && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, color: 'text.secondary' }}>
                    {t('permitSearch.pointCamera')}
                  </Typography>
                  <Box
                    component="video"
                    ref={videoRef}
                    autoPlay
                    playsInline
                    muted
                    sx={{
                      width: '100%',
                      maxWidth: 420,
                      height: 260,
                      objectFit: 'cover',
                      borderRadius: 1,
                      border: '1px solid #ddd',
                      bgcolor: '#000',
                    }}
                  />
                  {cameraLoading && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                      {t('permitSearch.initializingCamera')}
                    </Typography>
                  )}
                  {!!cameraInfo && !cameraLoading && (
                    <Typography variant="caption" sx={{ mt: 1, display: 'block', color: 'text.secondary' }}>
                      {cameraInfo}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>

            <Box sx={{ display: 'flex', gap: 2 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={handleSearch}
                disabled={loading || !searchQuery.trim()}
                sx={{
                  bgcolor: '#1976d2',
                  '&:hover': { bgcolor: '#1565c0' },
                }}
              >
                {loading ? t('permitSearch.searching') : t('common.search')}
              </Button>
              <Button
                variant="outlined"
                onClick={handleClear}
                disabled={loading}
              >
                {t('permitSearch.clear')}
              </Button>
            </Box>
          </form>

          {loading && <LinearProgress sx={{ mt: 2 }} />}
        </CardContent>
      </Card>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {scanMessage && (
        <Alert severity="success" sx={{ mb: 3 }} onClose={() => setScanMessage('')}>
          {scanMessage}
        </Alert>
      )}

      {/* Only shown when the tag is inactive AND there's no permit card to
          attach the warning to (unauthorized viewers get count: 0). When a
          card IS shown, the warning renders inside that card instead. */}
      {rfidWarning && results.length === 0 && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={() => setRfidWarning('')}>
          {rfidWarning}
        </Alert>
      )}

      {/* Results */}
      {searched && results.length > 0 && (
        <Box>
          {/* Summary */}
          <Card sx={{ mb: 3, bgcolor: '#f5f5f5' }}>
            <CardContent>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    {t('permitSearch.foundPermits', { count: results.length })}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6} sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                  {statusInfo && (
                    <Chip
                      icon={statusInfo.icon}
                      label={statusInfo.label}
                      color={statusInfo.color}
                      variant="outlined"
                    />
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Results Table */}
          {results.map((permit) => (
            <PermitDetailsCard
              key={permit.id}
              permit={permit}
              rfidInactive={!!rfidWarning}
              rfidWarningMessage={rfidWarning}
            />
          ))}
        </Box>
      )}

      {/* Empty State */}
      {searched && results.length === 0 && !error && !rfidWarning && (
        <Card sx={{ textAlign: 'center', py: 4 }}>
          <CardContent>
            <SearchIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" sx={{ color: 'text.secondary' }}>
              {t('permitSearch.noResultsTitle')}
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 1 }}>
              {t('permitSearch.noResultsSubtitle')}
            </Typography>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

// Icon + label + value tile — same visual language as the Permit Details
// page's info cards, so a permit reads consistently everywhere in the app.
const InfoTile = ({ icon: Icon, label, value, pillColor }) => (
  <Grid item xs={12} sm={6}>
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.5,
        p: 1.5,
        height: '100%',
        borderRadius: 2,
        border: '1px solid #e6e6e6',
        bgcolor: '#fafafa',
      }}
    >
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: 1.5,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: '#2e7d321f',
          color: '#2e7d32',
          flexShrink: 0,
        }}
      >
        <Icon sx={{ fontSize: 18 }} />
      </Box>
      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography
          variant="caption"
          sx={{
            color: '#8f8f8f',
            textTransform: 'uppercase',
            letterSpacing: '0.06em',
            fontSize: '0.66rem',
            fontWeight: 600,
            display: 'block',
            mb: 0.3,
          }}
        >
          {label}
        </Typography>
        {pillColor ? (
          <Box
            component="span"
            sx={{
              display: 'inline-block',
              px: 1.5,
              py: 0.3,
              borderRadius: 5,
              fontSize: '0.7rem',
              fontWeight: 700,
              color: '#fff',
              bgcolor: pillColor,
              textTransform: 'uppercase',
              letterSpacing: '0.03em',
            }}
          >
            {value}
          </Box>
        ) : (
          <Typography variant="body2" sx={{ fontWeight: 700, color: '#212121', wordBreak: 'break-word' }}>
            {value}
          </Typography>
        )}
      </Box>
    </Box>
  </Grid>
);

// Permit Details Card Component
const PermitDetailsCard = ({ permit, rfidInactive, rfidWarningMessage }) => {
  const { t } = useTranslation();
  const getDaysUntilExpiry = (expiryDate) => {
    const today = new Date();
    const expiry = new Date(expiryDate);
    const diffTime = expiry - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  // Status is authoritative here too — see getStatusInfo() above for why the
  // valid_to-based Expired/Expiring Soon downgrade is scoped to 'active' only.
  const getStatusColor = (status, validTo) => {
    if (status === 'expired' || status === 'cancelled') return '#f44336';
    if (status === 'pending') return '#ff9800';
    if (status === 'active') {
      const daysLeft = getDaysUntilExpiry(validTo);
      if (daysLeft < 0) return '#f44336';
      if (daysLeft < 30) return '#ff9800';
      return '#4caf50';
    }
    return '#2196f3';
  };

  const daysLeft = getDaysUntilExpiry(permit.valid_to);
  // A scanned RFID tag that's no longer linked to this permit isn't a
  // status of the permit itself — gray it out instead of the normal
  // green/amber/red so it visually reads as "not what you scanned for",
  // not as an actual pending/expiring/expired state.
  const statusColor = rfidInactive ? '#9e9e9e' : getStatusColor(permit.status, permit.valid_to);
  // Suspended/rejected permits get the same attention-grabbing red header
  // treatment as an inactive RFID scan — both mean "this permit isn't
  // usable right now", so the header should read the same way.
  // Backend status values: 'inactive' displays as "Suspended", 'cancelled'
  // displays as "Rejected" (see the common.inactive / common.cancelled
  // translation keys used for the STATUS pill below).
  const flaggedStatus = permit.status === 'inactive' || permit.status === 'cancelled';
  const headerAlert = rfidInactive || flaggedStatus;
  const totalValidityDays = permit.valid_from
    ? Math.max(1, Math.ceil((new Date(permit.valid_to) - new Date(permit.valid_from)) / (1000 * 60 * 60 * 24)))
    : null;
  const validityPercentage = daysLeft < 0
    ? 100
    : totalValidityDays
      ? Math.max(2, Math.min(100, (daysLeft / totalValidityDays) * 100))
      : Math.max(2, Math.min(100, daysLeft));

  return (
    <Card sx={{ mb: 3, borderLeft: `5px solid ${statusColor}` }}>
      <CardHeader
        title={t('permitSearch.permitPrefix', { number: permit.permit_number })}
        subheader={t('permitSearch.authorityPrefix', { authority: permit.authority })}
        action={headerAlert && (
          <Typography
            variant="body2"
            sx={{ color: '#fff', fontWeight: 'bold', maxWidth: 260, textAlign: 'right', mt: 1.5, mr: 1 }}
          >
            {rfidInactive ? rfidWarningMessage : t(`common.${permit.status}`, { defaultValue: permit.status })}
          </Typography>
        )}
        sx={headerAlert
          ? {
            bgcolor: '#d32f2f',
            '& .MuiCardHeader-title': { color: '#fff' },
            '& .MuiCardHeader-subheader': { color: 'rgba(255,255,255,0.85)' },
          }
          : { bgcolor: '#f5f5f5' }}
      />
      <CardContent>
        <Grid container spacing={3}>
          {/* Vehicle Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#1976d2' }}>
              {t('permitSearch.vehicleInfo')}
            </Typography>
            <Grid container spacing={1.5}>
              <InfoTile icon={VehicleIcon} label={t('permitSearch.vehicleNumberColon')} value={permit.vehicle_number} />
              <InfoTile icon={CategoryIcon} label={t('permitSearch.vehicleTypeColon')} value={permit.vehicle_type_name || t('permitSearch.na')} />
              <InfoTile
                icon={MakeIcon}
                label={t('permitSearch.makeModelColon')}
                value={permit.vehicle_make && permit.vehicle_model
                  ? `${permit.vehicle_make} ${permit.vehicle_model}`
                  : t('permitSearch.na')}
              />
              {permit.vehicle_year && (
                <InfoTile icon={CalendarIcon} label={t('permitSearch.yearColon')} value={permit.vehicle_year} />
              )}
            </Grid>
          </Grid>

          {/* Owner Information */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#1976d2' }}>
              {t('permitSearch.ownerInfo')}
            </Typography>
            <Grid container spacing={1.5}>
              <InfoTile icon={PersonIcon} label={t('permitSearch.ownerNameColon')} value={permit.owner_name} />
              <InfoTile icon={CnicIcon} label={t('permitSearch.cnicColon')} value={permit.owner_cnic || t('permitSearch.na')} />
              <InfoTile icon={EmailIcon} label={t('permitSearch.emailColon')} value={permit.owner_email} />
              <InfoTile icon={PhoneIcon} label={t('permitSearch.phoneColon')} value={permit.owner_phone} />
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <Divider />
          </Grid>

          {/* Permit Details */}
          <Grid item xs={12}>
            <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1.5, color: '#1976d2' }}>
              {t('permitSearch.permitDetails')}
            </Typography>
            <Grid container spacing={1.5}>
              <InfoTile icon={CategoryIcon} label={t('permitSearch.permitTypeColon')} value={permit.permit_type_name || t('permitSearch.na')} />
              <InfoTile
                icon={InfoIcon}
                label={t('permitSearch.statusColon')}
                value={t(`common.${permit.status}`, { defaultValue: permit.status })}
                pillColor={permit.status === 'active' ? '#2e7d32' : '#c62828'}
              />
              <InfoTile icon={EventAvailableIcon} label={t('permitSearch.validFromColon')} value={formatDate(permit.valid_from)} />
              <InfoTile icon={EventBusyIcon} label={t('permitSearch.validUntilColon')} value={formatDate(permit.valid_to)} />
            </Grid>
          </Grid>

          {/* Expiry / validity bar */}
          <Grid item xs={12}>
            <Box sx={{ p: 2, borderRadius: 2, border: '1px solid #e6e6e6', bgcolor: '#fafafa' }}>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'baseline', gap: 0.5, mb: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ color: '#8f8f8f', textTransform: 'uppercase', letterSpacing: '0.06em', fontSize: '0.68rem', fontWeight: 600 }}
                >
                  {t('permitSearch.daysRemainingColon')}
                </Typography>
              </Box>
              <Typography variant="h6" sx={{ fontWeight: 700, color: daysLeft < 0 ? '#f44336' : daysLeft < 30 ? '#ff9800' : '#4caf50', mb: 1 }}>
                {t('permitSearch.daysUnit', { count: Math.max(daysLeft, 0) })}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={validityPercentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e3e3e3',
                  '& .MuiLinearProgress-bar': { bgcolor: statusColor, borderRadius: 4 },
                }}
              />
              {permit.status === 'active' && daysLeft < 0 && (
                <Alert severity="error" sx={{ mt: 1.5, '*': { margin: 0 } }}>
                  {t('permitSearch.expiredAlert')}
                </Alert>
              )}
              {permit.status === 'active' && daysLeft >= 0 && daysLeft < 30 && (
                <Alert severity="warning" sx={{ mt: 1.5, '*': { margin: 0 } }}>
                  {t('permitSearch.expiringSoonAlert')}
                </Alert>
              )}
            </Box>
          </Grid>

          {permit.description && (
            <>
              <Grid item xs={12}>
                <Divider />
              </Grid>
              <Grid item xs={12}>
                <Grid container spacing={1.5}>
                  <InfoTile icon={DescriptionIcon} label={t('permitSearch.description')} value={permit.description} />
                </Grid>
              </Grid>
            </>
          )}

          {permit.approved_routes && (
            <Grid item xs={12}>
              <Grid container spacing={1.5}>
                <InfoTile icon={RouteIcon} label={t('permitSearch.approvedRoutes')} value={permit.approved_routes} />
              </Grid>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

export default PermitSearch;
